import AddIcon from "@mui/icons-material/Add";
import {
  QueryStats as AnalyzeIcon,
  AutoAwesome as ExtractIcon,
  CloudUpload as UploadIcon,
  FactCheck as ValidateIcon,
} from "@mui/icons-material";
import SchoolIcon from "@mui/icons-material/School";
import {
  alpha,
  Box,
  Button,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import type { SvgIconComponent } from "@mui/icons-material";
import { athenaColors } from "../landing/colors";
import { TUTORIAL_ENABLED } from "hooks/useOnboardingTutorial";
import { useTranslation } from "../../i18n";
import VideoGlassContainer from "./VideoGlassContainer";

interface WelcomeHeroCardProps {
  onCreatePatient: () => void;
  onStartTutorial: () => void;
}

interface Step {
  icon: SvgIconComponent;
  number: string;
  title: string;
  description: string;
}

export default function WelcomeHeroCard({
  onCreatePatient,
  onStartTutorial,
}: WelcomeHeroCardProps) {
  const { t } = useTranslation();

  const STEPS: Step[] = [
    {
      icon: UploadIcon,
      number: "01",
      title: t("welcome.step_upload_title", "Upload"),
      description: t("welcome.step_upload_desc", "Clinical documents, pathology reports, scans, or voice notes."),
    },
    {
      icon: ExtractIcon,
      number: "02",
      title: t("welcome.step_extract_title", "Extract"),
      description: t("welcome.step_extract_desc", "AI structures data across 44 cancer types automatically."),
    },
    {
      icon: ValidateIcon,
      number: "03",
      title: t("welcome.step_validate_title", "Validate"),
      description: t("welcome.step_validate_desc", "Review and approve extracted tables with inline editing."),
    },
    {
      icon: AnalyzeIcon,
      number: "04",
      title: t("welcome.step_analyze_title", "Analyze"),
      description: t("welcome.step_analyze_desc", "Dashboards, natural language queries, and cohort analysis."),
    },
  ];
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  return (
    <VideoGlassContainer
      glassSx={{
        display: "flex",
        flexDirection: "column",
        gap: { xs: 3, md: 4 },
        "& > *": { flexShrink: 0 },
      }}
    >
      {/* "A" watermark */}
      <Box
        component="img"
        src="/brand/icon/a_white_path.svg"
        alt=""
        sx={{
          position: "absolute",
          top: 12,
          right: 14,
          height: 26,
          width: "auto",
          opacity: 0.18,
          pointerEvents: "none",
        }}
      />

      {/* ── Row 1: Headline (left) + CTAs (right), stacked on small ── */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          gap: { xs: 3, md: 4 },
        }}
      >
        {/* Left column — text */}
        <Box
          sx={{
            flex: { md: 1 },
            width: { md: 0 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.65rem",
              letterSpacing: "0.14em",
              fontWeight: 700,
              color: athenaColors.periwinkle,
              textTransform: "uppercase",
              mb: 1,
            }}
          >
            Athena OncoCraft
          </Typography>

          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: athenaColors.white,
              lineHeight: 1.3,
              mb: 1.5,
            }}
          >
            {t("dashboard.empty_state.title", "No more manual entry.")}
          </Typography>

          <Typography
            sx={{
              fontSize: "0.85rem",
              color: alpha(athenaColors.white, 0.78),
              lineHeight: 1.6,
              maxWidth: 480,
            }}
          >
            {t(
              "dashboard.empty_state.subtitle",
              "Create your first patient to start using Athena OncoCraft. Upload clinical documents and let AI extract structured data automatically."
            )}
          </Typography>
        </Box>

        {/* Right column — CTAs */}
        <Box
          sx={{
            flex: { md: 1 },
            width: { xs: "100%", md: 0 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
          }}
        >
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onCreatePatient}
            sx={{
              background: athenaColors.royalBlue,
              color: athenaColors.white,
              borderRadius: 1.5,
              fontWeight: 600,
              fontSize: "0.85rem",
              py: 1,
              textTransform: "none",
              width: "100%",
              "&:hover": {
                background: alpha(athenaColors.royalBlue, 0.85),
              },
              transition: "all 0.18s ease",
            }}
          >
            {t("patients.create", "Create Patient")}
          </Button>

          <Tooltip
            title={
              TUTORIAL_ENABLED
                ? ""
                : t(
                    "tutorial.temporarily_unavailable",
                    "The guided tutorial is temporarily unavailable.",
                  )
            }
            arrow
          >
            {/* span: MUI does not fire tooltip events on a disabled button */}
            <span style={{ width: "75%", display: "inline-flex" }}>
          <Button
            variant="outlined"
            disabled={!TUTORIAL_ENABLED}
            startIcon={<SchoolIcon />}
            onClick={onStartTutorial}
            sx={{
              color: alpha(athenaColors.white, 0.85),
              borderColor: alpha(athenaColors.white, 0.3),
              "&.Mui-disabled": {
                color: alpha(athenaColors.white, 0.35),
                borderColor: alpha(athenaColors.white, 0.15),
              },
              borderRadius: 1.5,
              fontWeight: 600,
              fontSize: "0.85rem",
              py: 1,
              textTransform: "none",
              width: "75%",
              "&:hover": {
                borderColor: alpha(athenaColors.white, 0.55),
                background: alpha(athenaColors.white, 0.08),
              },
              transition: "all 0.18s ease",
            }}
          >
            {t("dashboard.empty_state.start_tutorial", "Start Tutorial")}
          </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Row 2: How It Works steps ── */}
      <Box>
        <Typography
          sx={{
            fontSize: "0.65rem",
            letterSpacing: "0.12em",
            fontWeight: 700,
            color: alpha(athenaColors.periwinkle, 0.6),
            textTransform: "uppercase",
            mb: 2,
            textAlign: "center",
          }}
        >
          {t("dashboard.empty_state.how_it_works", "HOW IT WORKS")}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1.5, sm: 1 },
            alignItems: { xs: "stretch", sm: "stretch" },
          }}
        >
          {STEPS.map((step) => {
            const Icon = step.icon;
            return (
              <Box
                key={step.number}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "row", sm: "column" },
                  alignItems: { xs: "center", sm: "stretch" },
                  flex: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "row", sm: "column" },
                    alignItems: { xs: "center", sm: "center" },
                    textAlign: { xs: "left", sm: "center" },
                    gap: { xs: 1.5, sm: 0 },
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: 2,
                    background: alpha(athenaColors.white, 0.08),
                    border: `1px solid ${alpha(athenaColors.white, 0.12)}`,
                    flex: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 36, sm: 40 },
                      height: { xs: 36, sm: 40 },
                      minWidth: { xs: 36, sm: 40 },
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: alpha(athenaColors.orange, 0.15),
                      border: `1px solid ${alpha(athenaColors.orange, 0.25)}`,
                      mb: { xs: 0, sm: 1 },
                      position: "relative",
                    }}
                  >
                    <Icon
                      sx={{
                        fontSize: { xs: 18, sm: 20 },
                        color: athenaColors.orange,
                      }}
                    />
                    <Typography
                      sx={{
                        position: "absolute",
                        top: -6,
                        right: -6,
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        background: alpha(athenaColors.orange, 0.15),
                        color: athenaColors.white,
                        fontWeight: 700,
                        fontSize: "0.55rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {step.number}
                    </Typography>
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: athenaColors.white,
                        fontSize: "0.8rem",
                        mb: 0.25,
                      }}
                    >
                      {step.title}
                    </Typography>
                    <Typography
                      sx={{
                        color: athenaColors.lightPeriwinkle,
                        fontSize: "0.72rem",
                        lineHeight: 1.4,
                      }}
                    >
                      {step.description}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </VideoGlassContainer>
  );
}
